import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import apiService from "../../services/api";

// Fallback blog posts if API fails
const fallbackBlogPosts = [
  {
    id: 1,
    title: "10 Reasons why you should visit New Jersey",
    excerpt: "Discover the hidden gems and amazing attractions that make New Jersey a must-visit destination for travelers.",
    featured_image: "/assets/img/blog/blog_1_1.jpg",
    published_at: "2024-07-05T00:00:00Z",
    slug: "10-reasons-visit-new-jersey",
  },
  {
    id: 2,
    title: "The best time to visit Japan & enjoy the cherry blossoms",
    excerpt: "Learn about the perfect timing to experience Japan's famous cherry blossom season and plan your trip accordingly.",
    featured_image: "/assets/img/blog/blog_1_2.jpg",
    published_at: "2024-07-06T00:00:00Z",
    slug: "best-time-visit-japan-cherry-blossoms",
  },
  {
    id: 3,
    title: "The 7 amazing destinations for adventure seekers",
    excerpt: "Explore thrilling destinations perfect for adrenaline junkies and adventure enthusiasts around the world.",
    featured_image: "/assets/img/blog/blog_1_3.jpg",
    published_at: "2024-07-07T00:00:00Z",
    slug: "7-amazing-destinations-adventure-seekers",
  },
];

function BlogOne() {
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBlogPosts = async () => {
      try {
        const response = await apiService.getAirTicketBlogPosts({ featured: true });
        if (response && response.results && response.results.length > 0) {
          setBlogPosts(response.results.slice(0, 6)); // Limit to 6 posts
        } else {
          // If no featured posts, don't show the section at all
          setBlogPosts([]);
        }
      } catch (error) {
        console.error('Error fetching blog posts:', error);
        setBlogPosts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogPosts();
  }, []);

  // Helper function to format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: '2-digit' 
    });
  };

  // Helper function to estimate read time
  const getReadTime = (content) => {
    if (!content) return "5 min read";
    const wordsPerMinute = 200;
    const wordCount = content.split(' ').length;
    const readTime = Math.ceil(wordCount / wordsPerMinute);
    return `${readTime} min read`;
  };

  if (loading) {
    return null; // Don't show anything during loading
  }

  // Don't render the section if there are no featured blog posts
  if (!blogPosts || blogPosts.length === 0) {
    return null;
  }

  return (
    <section className="bg-smoke overflow-hidden space overflow-hidden" id="blog-sec">
      <div className="container shape-mockup-wrap">
        <div className="mb-30 text-center text-md-start">
          <div className="row align-items-center justify-content-between">
            <div className="col-md-7">
              <div className="title-area mb-md-0">
                <span className="sub-title">About Us Restaurant</span>
                <h2 className="sec-title">News & Articles From Tourm</h2>
              </div>
            </div>
            <div className="col-md-auto">
              <Link to="/blog" className="th-btn style4 th-icon">
                See More Articles
              </Link>
            </div>
          </div>
        </div>

        {/* Swiper Slider */}
        <Swiper
          spaceBetween={20}
          slidesPerView={1}
          pagination={{ clickable: true }}
          breakpoints={{
            576: { slidesPerView: 1 },
            768: { slidesPerView: 2 },
            992: { slidesPerView: 2 },
            1200: { slidesPerView: 3 },
          }}
          className="swiper th-slider has-shadow"
        >
          {blogPosts.map((post) => {
            // Handle both API structure and fallback structure
            const postImage = post.featured_image || post.image || "/assets/img/blog/blog_1_1.jpg";
            const postDate = post.published_at ? formatDate(post.published_at) : (post.date || "Recent");
            const postReadTime = post.content ? getReadTime(post.content) : (post.readTime || "5 min read");
            const postLink = post.slug ? `/blog/${post.slug}` : (post.detailsLink || `/blog/${post.id}`);
            const postTitle = post.title || "Untitled Article";
            
            return (
              <SwiperSlide key={post.id}>
                <div className="blog-box th-ani">
                  <div className="blog-img global-img">
                    <img src={postImage} alt={postTitle} />
                  </div>
                  <div className="blog-box_content">
                    <div className="blog-meta">
                      <Link className="author" to="/blog">
                        {postDate}
                      </Link>
                      <Link to="/blog">{postReadTime}</Link>
                    </div>
                    <h3 className="box-title">
                      <Link to={postLink}>{postTitle}</Link>
                    </h3>
                    {post.excerpt && (
                      <p className="blog-excerpt">
                        {post.excerpt.length > 100 ? `${post.excerpt.substring(0, 100)}...` : post.excerpt}
                      </p>
                    )}
                    <Link to={postLink} className="th-btn style4 th-icon">
                      Read More
                    </Link>
                  </div>
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>

        {/* Decorative Shapes */}
        <div className="shape-mockup shape1 d-none d-xxl-block" style={{bottom:"20%", left:"-17%"}}>
          <img src="/assets/img/shape/shape_1.png" alt="shape" />
        </div>
        <div className="shape-mockup shape2 d-none d-xl-block" style={{bottom:"5%", left:"-17%"}}>
          <img src="/assets/img/shape/shape_2.png" alt="shape" />
        </div>
        <div className="shape-mockup shape3 d-none d-xxl-block" style={{bottom:"12%", left:"-10%"}}>
          <img src="/assets/img/shape/shape_3.png" alt="shape" />
        </div>
      </div>
    </section>
  );
}

export default BlogOne;
